/**
 * App entry — mounts React with Router + TanStack Query + AuthProvider.
 *
 * Routing:
 *   /login            → LoginScreen (unauthenticated)
 *   /                 → AppFrame (authenticated shell)
 *     /calls          → Dashboard (KPIs + enriched call table)
 *     /calls/:id      → CallDetail
 *     /manager        → ManagerView   (RequireRole manager|admin)
 *     /calibration    → Calibration   (RequireRole manager|admin)
 *
 * The same bundle boots in the web origin and the Chrome side-panel page;
 * platform.ts handles the shell differences (storage, layout density).
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  createHashRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import "@/index.css";
import { isExtension } from "@/platform";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { SimulationProvider } from "@/auth/SimulationProvider";
import { RequireRole } from "@/auth/RequireRole";
import { LoginScreen } from "@/auth/LoginScreen";
import { AppFrame } from "@/components/AppFrame";
import { LoadingState } from "@/components/LoadingState";
import { Dashboard } from "@/screens/Dashboard";
import { CallDetail } from "@/screens/CallDetail";
import { LiveCoach } from "@/screens/LiveCoach";
import { LiveMonitor } from "@/screens/LiveMonitor";
import { ManagerView } from "@/screens/ManagerView";
import { Calibration } from "@/screens/Calibration";
import { AgentProfile } from "@/screens/AgentProfile";
import { Admin } from "@/screens/admin/Admin";
import { SttCompare } from "@/screens/admin/SttCompare";
import { ProviderKeys } from "@/screens/admin/ProviderKeys";
import { Account } from "@/screens/Account";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/** Gate: render the app only when signed in; otherwise route to /login. */
function RequireAuth() {
  const { session, initializing } = useAuth();
  if (initializing) return <LoadingState label="Loading…" />;
  if (!session) return <Navigate to="/login" replace />;
  return <AppFrame />;
}

/** Send signed-in users away from /login. */
function LoginRoute() {
  const { session, initializing } = useAuth();
  if (initializing) return <LoadingState label="Loading…" />;
  if (session) return <Navigate to="/calls" replace />;
  return <LoginScreen />;
}

/** Gate the live-monitor firehose on the per-user `can_view_live_monitor` toggle
 *  (set in the admin panel). UX gate only — the WebSocket re-checks it server-side. */
function RequireLiveMonitor() {
  const { session, initializing, canViewLiveMonitor } = useAuth();
  if (initializing) return <LoadingState label="Checking access…" />;
  if (!session) return <Navigate to="/login" replace />;
  if (!canViewLiveMonitor) return <Navigate to="/calls" replace />;
  return <LiveMonitor />;
}

const routes = [
  { path: "/login", element: <LoginRoute /> },
  // M3 LIVE in-call coaching. ADDITIVE + intentionally OUTSIDE RequireAuth/
  // RequireRole so it renders in the bare side-panel shell during a call. It
  // still needs a signed-in Supabase session for the WebSocket token (it shows a
  // friendly "sign in" state otherwise) — and ?demo=1 replays a sample call with
  // no server. Lives under AuthProvider (all routes do), so useAuth() is safe.
  { path: "/live", element: <LiveCoach /> },
  // THREE-COLUMN live call-monitor "firehose" — a debug view of ALL calls.
  // Gated by the per-user `can_view_live_monitor` permission (admin-managed in the
  // admin panel). The WebSocket re-checks the same permission server-side.
  { path: "/live-monitor", element: <RequireLiveMonitor /> },
  {
    path: "/",
    element: <RequireAuth />,
    children: [
      { index: true, element: <Navigate to="/calls" replace /> },
      { path: "calls", element: <Dashboard /> },
      { path: "calls/:id", element: <CallDetail /> },
      { path: "account", element: <Account /> },
      {
        path: "manager",
        element: (
          <RequireRole allow={["manager", "admin"]}>
            <ManagerView />
          </RequireRole>
        ),
      },
      {
        // CP3 agent profile — a DRILL-IN under Coaching (no Sidebar item; the
        // /manager NavLink stays active here). Manager/admin only; the server
        // enforces the role + the call-log blocklist for non-admins.
        path: "manager/agents/:id",
        element: (
          <RequireRole allow={["manager", "admin"]}>
            <AgentProfile />
          </RequireRole>
        ),
      },
      {
        // CP1 calibration — per-rep self-vs-AI gap. Manager/admin only (the
        // server also enforces the role + the call-log blocklist for non-admins).
        path: "calibration",
        element: (
          <RequireRole allow={["manager", "admin"]}>
            <Calibration />
          </RequireRole>
        ),
      },
      {
        // ONE consolidated admin panel (Users & Access + the AI tabs +
        // Call-log privacy). RequireRole admin-gates the whole panel; each
        // server endpoint re-enforces the role too.
        path: "admin",
        element: (
          <RequireRole allow={["admin"]}>
            <Admin />
          </RequireRole>
        ),
      },
      {
        // Back-compat: the old AI Settings route now lives as a tab on /admin.
        // Land on the grader tab to match where /ai-settings used to open.
        path: "ai-settings",
        element: <Navigate to="/admin?tab=grader" replace />,
      },
      {
        path: "stt-compare",
        element: (
          <RequireRole allow={["manager", "admin"]}>
            <SttCompare />
          </RequireRole>
        ),
      },
      {
        path: "api-keys",
        element: (
          <RequireRole allow={["admin"]}>
            <ProviderKeys />
          </RequireRole>
        ),
      },
    ],
  },
  { path: "*", element: <Navigate to="/calls" replace /> },
];

// The extension side-panel is served from chrome-extension://<id>/index.html,
// where the History API base is awkward — use a hash router there.
const router = isExtension() ? createHashRouter(routes) : createBrowserRouter(routes);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SimulationProvider>
          <RouterProvider router={router} />
        </SimulationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error('Missing #root element in index.html');

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
